import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ApiClient from '../../methods/api/apiClient';
import { shadeColor, tintFilter } from './whiteLabelCopy';

const WhiteLabelContext = createContext({
  agencyTheme: {
    sidebarColor: null,
    buttonColor: null,
    logoUrl: null,
    agencyName: null,
  },
  loading: false,
});
const WL_SLUG_KEY = 'whiteLabelAgencySlug';

export const setWhiteLabelSlug = (slug) => {
  try { sessionStorage.setItem(WL_SLUG_KEY, slug); } catch (e) {}
};

export const getWhiteLabelSlug = () => {
  try { return sessionStorage.getItem(WL_SLUG_KEY); } catch (e) { return null; }
};

const getHostSlug = () => {
  try {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www') return parts[0];
    if (parts.length === 2 && parts[1] === 'localhost' && parts[0] !== 'www') return parts[0];
  } catch (e) {}
  return null;
};

export const useWhiteLabel = () => useContext(WhiteLabelContext);

const FRONT_URL = process.env.REACT_APP_API_URL || 'http://localhost:6089';

const DEFAULT_FAVICON = '/assets/img/favicon-16x16.png';

const resolveUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${FRONT_URL}/img/${path}`;
};

const applyFavicon = (faviconUrl) => {
  let link = document.querySelector('link[rel="icon"]');
  if (faviconUrl) {
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  } else {
    if (link) link.remove();
  }
};

const applyTheme = (theme) => {
  if (!theme) {
    document.documentElement.style.removeProperty('--sidebar-bg');
    document.documentElement.style.removeProperty('--button-bg');
    document.documentElement.style.removeProperty('--button-bg-hover');
    document.documentElement.style.removeProperty('--brand-name');
    document.documentElement.style.removeProperty('--brand');
    document.documentElement.style.removeProperty('--brand-hover');
    document.documentElement.style.removeProperty('--brand-light');
    document.documentElement.style.removeProperty('--brand-pale');
    document.documentElement.style.removeProperty('--brand-pale-2');
    document.documentElement.style.removeProperty('--brand-rgb');
    document.documentElement.style.removeProperty('--icon-hue');
    document.documentElement.style.removeProperty('--icon-sat');
    document.documentElement.style.removeProperty('--icon-bright');
    document.documentElement.style.removeProperty('--heart-hue');
    document.documentElement.style.removeProperty('--heart-sat');
    document.documentElement.style.removeProperty('--heart-bright');
    applyFavicon(DEFAULT_FAVICON);
    return;
  }
  const rgb = shadeColor(theme.buttonColor, 0).replace('#', '');
  const r = parseInt(rgb.slice(0, 2), 16);
  const g = parseInt(rgb.slice(2, 4), 16);
  const b = parseInt(rgb.slice(4, 6), 16);
  const tint = tintFilter('#7C3AED', theme.buttonColor);
  const heartTint = tintFilter('#986DCD', theme.buttonColor);
  document.documentElement.style.setProperty('--sidebar-bg', theme.sidebarColor);
  document.documentElement.style.setProperty('--button-bg', theme.buttonColor);
  document.documentElement.style.setProperty('--button-bg-hover', shadeColor(theme.buttonColor, -15));
  document.documentElement.style.setProperty('--brand-name', `"${theme.agencyName}"`);
  document.documentElement.style.setProperty('--brand', theme.buttonColor);
  document.documentElement.style.setProperty('--brand-hover', shadeColor(theme.buttonColor, -15));
  document.documentElement.style.setProperty('--brand-light', shadeColor(theme.buttonColor, 20));
  document.documentElement.style.setProperty('--brand-pale', shadeColor(theme.buttonColor, 88));
  document.documentElement.style.setProperty('--brand-pale-2', shadeColor(theme.buttonColor, 80));
  document.documentElement.style.setProperty('--brand-rgb', `${r}, ${g}, ${b}`);
  document.documentElement.style.setProperty('--icon-hue', `${tint.hue}deg`);
  document.documentElement.style.setProperty('--icon-sat', tint.sat);
  document.documentElement.style.setProperty('--icon-bright', tint.bright);
  document.documentElement.style.setProperty('--heart-hue', `${heartTint.hue}deg`);
  document.documentElement.style.setProperty('--heart-sat', heartTint.sat);
  document.documentElement.style.setProperty('--heart-bright', heartTint.bright);
  applyFavicon(theme.faviconUrl);
  // Mettre à jour les images de la sidebar directement
  ['sidebar-logo-img', 'sidebar-logo-a-img'].forEach((id) => {
    const el = document.getElementById(id);
    if (el && theme.logoUrl) el.src = theme.logoUrl;
  });
};

export const WhiteLabelProvider = ({ children }) => {
  const { user } = useSelector((state) => state);
  const [agencyTheme, setAgencyTheme] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const agencyId = user?.whiteLabelAgencyId;
    const slug = getWhiteLabelSlug() || getHostSlug();
    if (slug) setWhiteLabelSlug(slug);

    // Si connecté avec une agence white-label
    if (agencyId) {
      setLoading(true);
      ApiClient.get(`/user/detail?id=${agencyId}`)
        .then((res) => {
          if (res?.success) {
            const a = res.data;
            const logoPath = a.companyLogo || a.agencyLogo || '';
            const theme = {
              sidebarColor: a.sidebarColor || '#976DD0',
              buttonColor: a.buttonColor || '#976DD0',
              logoUrl: resolveUrl(logoPath),
              faviconUrl: resolveUrl(a.favicon),
              agencyName: a.agencyName || a.companyName || a.fullName || '',
            };
            setAgencyTheme(theme);
            applyTheme(theme);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }

    // Guest / non-connecté : charger le thème depuis le slug stocké
    if (slug) {
      setLoading(true);
      ApiClient.get(`/api/public/agency/${slug}`)
        .then((res) => {
          if (res?.success) {
            const a = res.data;
            const logoPath = a.companyLogo || a.agencyLogo || '';
            const theme = {
              sidebarColor: a.sidebarColor || '#976DD0',
              buttonColor: a.buttonColor || '#976DD0',
              logoUrl: resolveUrl(logoPath),
              faviconUrl: resolveUrl(a.favicon),
              agencyName: a.agencyName || a.companyName || a.fullName || '',
            };
            setAgencyTheme(theme);
            applyTheme(theme);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }

    // Aucun contexte white-label
    applyTheme(null);
    setAgencyTheme(null);
  }, [user?.whiteLabelAgencyId]);

  return (
    <WhiteLabelContext.Provider value={{ agencyTheme, loading }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
